import { useCallback } from 'react';
import { formatTextInput } from '../utils/dataFormatting';

const FIELD_MAPPINGS = {
  name: { pickup: 'pickup_name', billing: 'billing_name' },
  establishment_address1: { pickup: 'pickup_address1', billing: 'billing_address1' },
  establishment_address2: { pickup: 'pickup_address2', billing: 'billing_address2' },
  establishment_postcode: { pickup: 'pickup_postcode', billing: 'billing_postcode' },
  establishment_city: { pickup: 'pickup_city', billing: 'billing_city' },
  establishment_country: { pickup: 'pickup_country', billing: 'billing_country' },
  establishment_contact: { pickup: 'pickup_contact', billing: 'billing_contact' },
  establishment_phone1: { pickup: 'pickup_phone1', billing: 'billing_phone1' },
  establishment_phone2: { pickup: 'pickup_phone2', billing: 'billing_phone2' },
  establishment_email: { pickup: 'pickup_email', billing: 'billing_email' },
};

const ESTABLISHMENT_FIELDS = Object.keys(FIELD_MAPPINGS);

const isSectionEmptyOrIdentical = (formData, sectionPrefix) => {
  let allEmpty = true;
  let allIdentical = true;

  for (const [estField, mappings] of Object.entries(FIELD_MAPPINGS)) {
    const targetField = mappings[sectionPrefix];
    if (!targetField) continue;

    const estValue = estField === 'name' ? formData.name || '' : formData[estField] || '';
    const targetValue = formData[targetField] || '';

    if (targetValue !== '') {
      allEmpty = false;
    }

    if (targetValue !== estValue) {
      allIdentical = false;
    }
  }

  // Propager seulement si TOUS les champs sont vides OU TOUS sont identiques
  return allEmpty || allIdentical;
};

export const useEstablishmentSync = (
  formData,
  setFormData,
  handleChange,
  entityType,
  setHasChanged,
  isLoading
) => {
  const handleChangeWithSync = useCallback(
    e => {
      const { name, type, checked } = e.target;
      const isCheckbox = type === 'checkbox';

      // Calculer la valeur formatée comme dans handleChange
      let value = e.target.value || '';
      if (!isCheckbox) {
        value = formatTextInput(value);
      }
      const newValue = isCheckbox ? checked : value;

      // Vérifier si c'est un champ établissement
      if (!ESTABLISHMENT_FIELDS.includes(name)) {
        // Pas un champ établissement, utiliser handleChange normal
        handleChange(e);
        return;
      }

      const mapping = FIELD_MAPPINGS[name];
      if (!mapping) {
        handleChange(e);
        return;
      }

      // Marquer le formulaire comme modifié
      if (!isLoading) {
        setHasChanged(true);
      }

      // Utiliser setFormData avec callback pour avoir l'état actuel
      setFormData(prev => {
        const updates = { [name]: newValue };

        // Vérifier et propager vers pickup (seulement pour client)
        if (entityType === 'client' && mapping.pickup) {
          if (isSectionEmptyOrIdentical(prev, 'pickup')) {
            updates[mapping.pickup] = newValue;
          }
        }

        // Vérifier et propager vers billing
        if (mapping.billing) {
          if (isSectionEmptyOrIdentical(prev, 'billing')) {
            updates[mapping.billing] = newValue;
          }
        }

        return { ...prev, ...updates };
      });
    },
    [setFormData, handleChange, entityType, setHasChanged, isLoading]
  );

  return { handleChangeWithSync };
};
